import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchCachedItems } from "store/actions/itemsActions";
import AddToCart from "./AddToCart";
import { checkFinalPrice } from "./checkFinalPrice";
import { useHolidayGuard } from "hooks/useHolidayGuard";
import ProductDetailsSection from "./ProductsDetailModal/ProductDetailsSection";

const Items = ({ categoryId }) => {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { itemCache } = useSelector((state) => state.item);
  const { cartItems } = useSelector((state) => state.cart);
  const { restaurantsDetail } = useSelector((state) => state.restaurant);

  const [filteredItems, setFilteredItems] = useState(itemCache);
  const [isFetching, setIsFetching] = useState(false);
  const [showAddToCartModal, setAddToCartModal] = useState(false);
  const [showItem, setShowItem] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [viewItemModal, setViewItemModal] = useState(false);

  // Compute cacheKey same as used in fetchCachedItems
  const cacheKey = `${user?.restaurantID}_${categoryId}`;
  const cachedItems = itemCache?.[cacheKey] || [];

  useEffect(() => {
    setIsFetching(true);
    let unsubscribe = null;

    const setupListener = async () => {
      const unsub = await dispatch(
        fetchCachedItems({ restaurantID: user?.restaurantID, categoryId })
      );
      unsubscribe = unsub;
    };

    setupListener();

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [user?.restaurantID, categoryId, dispatch]);

  useEffect(() => {
    if (cachedItems.length > 0) {
      setFilteredItems(cachedItems);
      setIsFetching(false);
    }
  }, [cachedItems]);

   const { holidayStatus, checkAndToast } = useHolidayGuard(
        restaurantsDetail,
        "dinein"
      );

  const addToCartModalToggler = (itemPrice = 0) => {
    if(checkAndToast()) return;
    if (showAddToCartModal) {

    } else if (itemPrice) {
      const simulatedValue = checkFinalPrice({
        cartItems,
        restaurant: restaurantsDetail,
        itemPrice: itemPrice,
      });

      if (simulatedValue > 10000) {
        toast.warning(
          "Not enough space in the cart. Try removing the items or adjust the quantity. Max order limit is 10000"
        );
        return;
      }
    }
    setAddToCartModal(!showAddToCartModal);
  };

  const ViewItemModalToggler = () => {
    if (viewItemModal) {
      setActiveItem(null);
    }
    setViewItemModal(!viewItemModal);
  };

  return (
    <>
      <CardBody className="bottomPadding-0">
        {isFetching ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner color="primary" />
          </div>
        ) : filteredItems?.length > 0 ? (
          <Row className="p-0">
            {filteredItems?.map((item) => (
              <Col
                key={item?.id}
                xxl={3}
                xl={3}
                lg={4}
                md={6}
                sm={6}
                xs={12}
                style={{ marginBottom: "19px" }}
              >
                <Card
                  className="w-100 h-100 overflow-hidden border border-gray bg-secondary"
                  style={{ boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)" }}
                >
                  <CardHeader
                    className="relative w-100 p-0 overflow-hidden"
                    style={{ height: "175px" }}
                  >
                    {!item?.isVisible && (
                      <Badge
                        color="danger"
                        className="position-absolute top-2 left-2"
                      >
                        Hidden
                      </Badge>
                    )}
                    <img
                      src={
                        item?.images?.length
                          ? item?.images[0]
                          : "https://res.cloudinary.com/dzhvjtoot/image/upload/v1712309249/logo/ogb3puh0buzqtzem3ce1.png"
                      }
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                      alt="item img"
                    />
                  </CardHeader>
                  <CardBody className="px-3 py-2 d-flex flex-column justify-content-between">
                    <div>
                      <CardTitle tag="h3" className="mb-0 p-0">
                        {item?.name}
                      </CardTitle>
                      <div style={{ fontSize: "12px" }} className="mt-1">
                        <div>
                          <i className="fa fa-clock mr-1" />
                          {item?.estimatedTime} (mins)
                        </div>
                        <div className="d-flex mt-2">
                          {item?.fullLabels?.length > 0 &&
                            item.fullLabels.map((lbl) => {
                              return (
                                lbl?.labelURL && (
                                  <img
                                    key={lbl.labelName}
                                    src={lbl.labelURL}
                                    alt={lbl.labelName}
                                    title={lbl.labelName}
                                    style={{
                                      width: 25,
                                      height: 25,
                                      marginRight: 6,
                                      objectFit: "contain",
                                    }}
                                  />
                                )
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </CardBody>
                  <div className="p-0 d-flex align-items-center justify-content-center"> 
                    <div className="d-flex align-items-center w-100 p-2" style={{gap : "8px"}}>
                      <Button
                        color="success"
                        className="w-50 d-flex align-items-center justify-content-center px-1 mx-0"
                        size="sm"
                        onClick={() => {
                          setActiveItem(item);
                          ViewItemModalToggler();
                        }}
                      >
                        VIEW DETAILS
                      </Button>
                      <Button
                        color="primary"
                        className="w-50 d-flex align-items-center justify-content-center mx-0"
                        size="sm"
                        onClick={() => {
                          setShowItem(item);
                          addToCartModalToggler(
                            Math.min(
                              ...(item?.variants?.map((v) =>
                                parseFloat(v.price)
                              ) || [])
                            )
                          );
                        }}
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="d-flex flex-column justify-content-center align-items-center py-5">
            No Item found
          </div>
        )}
      </CardBody>

      {viewItemModal && (
        <ProductDetailsSection
          isOpen={viewItemModal}
          toggle={ViewItemModalToggler}
          data={activeItem}
        />
      )}

      <AddToCart
        isOpen={showAddToCartModal}
        toggler={addToCartModalToggler}
        showItem={showItem}
        type="item"
      />
    </>
  );
};

export default Items;
